// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Button.tsx
 * @input Uses React, ButtonHTMLAttributes, ReactNode, i18n (useTranslator)
 * @output Exports Button component, ButtonProps, ButtonVariant types
 * @position Core implementation; consumed by index.ts, tested by Button.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Button/Button.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Button/Button.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Button/index.ts (exports if types change)
 * - /apps/storybook/stories/Button.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/Button/ (showcase blocks)
 *
 * Last synced props: label, variant, size, isDisabled, isLoading, isInterruptible, clickAction, icon, isIconOnly, width, children, tooltip, endContent, href, as, target, rel
 */
import { useRef, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useTooltip } from "../Tooltip/useTooltip.js";
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, spacingVars, radiusVars, borderVars, durationVars, easeVars, fontWeightVars, typeScaleVars, shadowVars } from "../theme/tokens.stylex.js";
import { Spinner } from "../Spinner/index.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { EDGE_COMP_ATTR } from "../Layout/edgeCompensation.stylex.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { useButtonGroup } from "../ButtonGroup/ButtonGroupContext.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Base button styles
 * Pseudo-classes are nested within properties per StyleX recommendation:
 * https://stylexjs.com/docs/learn/styling-ui/defining-styles#pseudo-classes
 */
const styles = {
  base: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kOIVth: "x1txdalj",
    k8WAf4: "xce4md1",
    kg3NbH: "xrrkdod",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kaIpWk: "x1jxw6zd",
    kMv6JI: "xjb2p0i",
    kGuDYH: "xcr08ib",
    kLWn49: "x1kq96og",
    k63SB2: "x1e4wzip",
    khDVqt: "xuxw1ft",
    kkrTdU: "x1ypdohk",
    k1ekBW: "xrafxwg",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  pressable: {
    k3aq6I: "x3oybdh xk4oym4",
    $$css: true
  },
  disabled: {
    kkrTdU: "x1h6gzvc",
    kSiTet: "xbyyjgo",
    kKwaWg: "x18o3ruo",
    k3aq6I: "x1c071of x1pdlv7q",
    $$css: true
  },
  ariaDisabled: {
    kKwaWg: "x18o3ruo x8o3jvd xuqm82a",
    $$css: true
  },
  iconOnly: {
    "--button-icon-only-aspect": "x1v15ycx",
    kOBAk4: "xioom0i",
    kg3NbH: "xnjsko4",
    k8WAf4: "xt970qd",
    $$css: true
  },
  iconWrapper: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    $$css: true
  },
  contentWrapper: {
    k1xSpc: "xjp7ctv",
    $$css: true
  },
  link: {
    kybGjl: "x1hl2dhg",
    $$css: true
  }
};

// Dynamic style for the consumer-controlled button width. Numbers are treated
// as pixels by StyleX; strings (e.g. '100%') are used as-is.
const dynamicStyles = {
  width: width => [{
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }]
};
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};

/**
 * Icon size per button size.
 * Matches Icon sizing: sm/md=16px, lg=20px.
 * fontSize is set so emoji and text-based icons scale correctly.
 */
const iconSizeStyles = {
  sm: {
    kzqmXN: "x1kky2od",
    kZKoxP: "xlup9mm",
    kGuDYH: "x1j61zf2",
    $$css: true
  },
  md: {
    kzqmXN: "x1kky2od",
    kZKoxP: "xlup9mm",
    kGuDYH: "x1j61zf2",
    $$css: true
  },
  lg: {
    kzqmXN: "xw4jnvo",
    kZKoxP: "x1qx5ct2",
    kGuDYH: "xwsyq91",
    $$css: true
  }
};

/**
 * Resting elevation for floating buttons (e.g. a FAB). `none` is the default
 * flat button; `low`/`med`/`high` map to the shadow token scale. 'none' stays
 * a literal so it never conflicts with a variant's background layering.
 */
const elevationStyles = {
  none: {
    kGVxlE: "x1gnnqk1",
    $$css: true
  },
  low: {
    kGVxlE: "x1i5ehqx",
    $$css: true
  },
  med: {
    kGVxlE: "x14hfi27",
    $$css: true
  },
  high: {
    kGVxlE: "x1kcpxr7",
    $$css: true
  }
};

/**
 * Variant styles using backgroundImage for layered colors
 * Pseudo-classes are nested within properties per StyleX recommendation
 * Overlay is stacked on top of base color using multiple linear-gradients
 * Focus outline color matches variant (destructive uses negative color)
 */
const variants = {
  primary: {
    kWkggS: "x1ewilqj",
    kMwMTN: "x17wrial",
    kKwaWg: "x1ilzqfv xq8i9tn",
    kI3sdo: "x17nn4n9",
    "--button-focus-offset": "xkzo27j",
    kInvED: "x1wfwxd8 x13aywxo",
    $$css: true
  },
  secondary: {
    kWkggS: "x17x4s8c",
    kMwMTN: "x1tgivj0",
    kKwaWg: "x1ilzqfv xq8i9tn",
    kI3sdo: "x17nn4n9",
    "--button-focus-offset": "xkzo27j",
    kInvED: "x1wfwxd8 x13aywxo",
    $$css: true
  },
  ghost: {
    kWkggS: "xjbqb8w",
    kMwMTN: "x1tgivj0",
    kKwaWg: "x1ilzqfv xq8i9tn",
    kI3sdo: "x17nn4n9",
    "--button-focus-offset": "xkzo27j",
    kInvED: "x1wfwxd8 x13aywxo",
    $$css: true
  },
  destructive: {
    kWkggS: "x1pjz0fi",
    kMwMTN: "x1m024r3",
    kKwaWg: "x1ilzqfv xq8i9tn",
    kI3sdo: "x1p73he7",
    "--button-focus-offset": "xkzo27j",
    kInvED: "x1wfwxd8 x13aywxo",
    $$css: true
  }
};

/**
 * Button variant type derived from ButtonVariantMap.
 * Extensible via module augmentation of ButtonVariantMap.
 */

/**
 * Button size type derived from the sizeStyles StyleX object
 */

const spinnerReveal = "x18re5ia-B";
const contentHide = "x188b6p3-B";

// Hold the loading swap for a short delay so a fast action (e.g. clickAction)
// that settles within the delay never flashes a spinner. The spinner fade-in
// and the content hide share the same delay so the button never shows an empty
// frame in between. Reduced motion is instant.
const SPINNER_DELAY = durationVars['--duration-medium-min'];
const loadingStyles = {
  hiddenContent: {
    kMwMTN: "x19co3pv",
    $$css: true
  },
  hiddenContentDelayed: {
    kKVMdj: "x1ffowhz",
    k44tkh: "xjlvqhv",
    kWV6AL: "x10e4vud",
    kKxzle: "x17yabm6 x14q22ui",
    $$css: true
  }
};

/**
 * "I am the last member of the group" — the trailing end cap.
 *
 * NOT `:last-child`: several members render an invisible layer element AFTER
 * their button (a tooltip'd Button returns button + layer; DropdownMenu returns
 * trigger + popover). `useLayer` renders those inline rather than portaling
 * them, so the layer — not the button — took the `:last-child` slot and the real
 * trailing button silently kept square corners (#2508).
 *
 * Layers always carry the native `popover` attribute (useLayer.tsx), and a
 * popover is never an in-flow member — it is `display: none` until shown, then
 * promoted to the top layer. So "last member" is: no following element sibling
 * that isn't a popover.
 *
 * Reading it the other way round — marking the *buttons* and testing for a
 * marked sibling — is the trap: it silently reclassifies anything it doesn't
 * recognise as "not a member", so a member wrapped in a `display: contents`
 * wrapper (Tooltip, HoverCard) or a raw child would make the button BEFORE it
 * round mid-group. Ignoring known layers keeps the predicate conservative: an
 * unrecognised sibling still counts, exactly as `:last-child` did, so the worst
 * case degrades to the old behaviour instead of a wrong corner.
 *
 * Kept as a same-file const: StyleX only statically evaluates a selector key
 * from a const in the same file.
 *
 * The leading edge still uses `:first-child` — a member's button always precedes
 * its own layer, so the first button is genuinely `:first-child`.
 */
const IS_LAST_ITEM = ':not(:has(~ *:not([popover])))';
const groupStyles = {
  horizontal: {
    krdFHd: "x15mokao x8eehn2",
    kVL7Gh: "xbiv7yw x1xrp5p4",
    kfmiAY: "x1ga7v0g xbfdvlp",
    kT0f0o: "x16uus16 xajhecq",
    k2ei4v: "xgbv0en x1pjv70x",
    kVhnKS: "x1t7ytsu xyf0ibl",
    kGJrpR: "x1j92z86",
    $$css: true
  },
  vertical: {
    krdFHd: "x15mokao x8eehn2",
    kfmiAY: "x1ga7v0g x2qxyot",
    kVL7Gh: "xbiv7yw x12tyzlf",
    kT0f0o: "x16uus16 xajhecq",
    kEafiO: "x11xkdxz x1g31smg",
    kPef9Z: "x13fuv20 x1d9v4yf",
    kLZC3w: "x1pc3f07",
    $$css: true
  },
  onSolidHorizontal: {
    kGJrpR: "xrvmtm5",
    $$css: true
  },
  onSolidVertical: {
    kLZC3w: "x11npmm7",
    $$css: true
  }
};

/**
 * A versatile button component with multiple variants.
 *
 * Styles use Astryx theme tokens via StyleX.
 * Wrap your app in <Theme> to apply a theme.
 * Themes can provide component-level variant overrides via theme.components.button.variants
 *
 * When `href` is provided (and the button is not disabled), renders as an `<a>`
 * element (or custom link component) with full button styling, enabling native
 * browser behaviors like right-click → open in new tab and Cmd+Click.
 *
 * @example
 * ```
 * <Button label="Click me" />
 * <Button label="Primary action" variant="primary" />
 * <Button label="Delete" variant="destructive" />
 * <Button label="Settings" icon={<GearIcon />} variant="ghost" isIconOnly />
 * <Button label="Pick emoji" icon={<span>🚀</span>} variant="ghost" size="sm" isIconOnly />
 * <Button label="Edit" icon={<PencilIcon />} />
 * <Button label="Messages" endContent={<Badge label={3} />} />
 * <Button label="Edit" icon={<PencilIcon />} endContent={<Badge label="New" />} />
 * <Button label="Sign in" variant="primary" width="100%" />
 * <Button label="Visit site" href="https://example.com" variant="primary" />
 * <Button label="Open in new tab" href="https://example.com" target="_blank" rel="noopener noreferrer" />
 * ```
 */
export function Button({
  label,
  variant = 'secondary',
  size: sizeProp,
  type = 'button',
  isDisabled = false,
  isLoading = false,
  isInterruptible = false,
  clickAction,
  icon,
  isIconOnly = false,
  width,
  elevation = 'none',
  children,
  endContent,
  tooltip,
  href,
  as,
  target,
  rel,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  const t = useTranslator();
  const size = useSize(sizeProp, 'md');
  const buttonGroup = useButtonGroup();
  const [isPending, startTransition] = useTransition();
  // clickAction is normally fire-once (submit/save/pay), so a same-tick
  // double-click must dedupe — which neither isPending nor useOptimistic do.
  // Hence the ref guard. Interruptible callers (e.g. ToggleButton) opt out so a
  // re-click can land and interrupt the in-flight action with a fresh one.
  const actionInFlightRef = useRef(false);
  const isLoadingState = isLoading || isPending;
  // Delay the spinner reveal for action-driven loading (clickAction's own
  // transition) so a fast action that settles within the delay does not flash
  // a spinner. Interruptible loading is delayed too, so rapid re-clicks settle
  // before any spinner shows. Explicit isLoading-only stays immediate, since
  // the consumer is deliberately showing it.
  const delaySpinner = isPending || isInterruptible;
  const groupDisabled = buttonGroup?.isDisabled ?? false;
  // When interruptible, the loading state drives the spinner and aria-busy but
  // not disabled, so clicks keep landing and can interrupt the in-flight action.
  const buttonDisabled = isDisabled || groupDisabled || isLoadingState && !isInterruptible;
  // isIconOnly prop is the source of truth for icon-only rendering.
  // When false (default), label is always rendered as visible text.

  const LinkComponent = useLinkComponent(as);

  // Render as link when href is provided and button is not disabled.
  // Disabled links are an accessibility anti-pattern — fall back to <button>.
  const renderAsLink = href != null && !buttonDisabled;

  // Use aria-disabled when tooltip is present so the button remains focusable
  // for keyboard users to reach the tooltip. Otherwise use native disabled.
  const useAriaDisabled = tooltip != null && buttonDisabled;

  // Attach tooltip behavior via the hook rather than wrapping the button in a
  // <Tooltip> element. The hook adds hover/focus triggers to the button itself,
  // so no extra DOM node is inserted — the button stays a direct child of its
  // container (no layout shift, and edge-compensation markers remain
  // discoverable through the container's direct-child `:has()` selector).
  const tooltipHook = useTooltip({
    placement: 'above',
    isEnabled: tooltip != null
  });
  const handleClick = e => {
    // The ref guard dedupes fire-once actions. Interruptible callers skip it so
    // a re-click while pending starts a fresh action that interrupts the prior.
    if (buttonDisabled || actionInFlightRef.current && !isInterruptible) {
      e.preventDefault();
      return;
    }
    props.onClick?.(e);
    if (clickAction && !e.defaultPrevented) {
      actionInFlightRef.current = true;
      startTransition(async () => {
        try {
          await clickAction(e);
        } finally {
          actionInFlightRef.current = false;
        }
      });
    }
  };

  // When aria-disabled, suppress activation keys (Enter/Space) but allow
  // other keys (Escape, arrows) to reach consumer handlers.
  const handleKeyDown = useAriaDisabled ? e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
    } else {
      props.onKeyDown?.(e);
    }
  } : undefined;

  // Ghost buttons are edge-compensatable — containers detect this attribute
  // via :has() and pull their own slot margins to align flush at edges.
  const isFlat = variant === 'ghost';
  const edgeCompAttr = isFlat ? {
    [EDGE_COMP_ATTR]: ''
  } : null;

  // Shared StyleX props for both button and link rendering
  const sharedStylexProps = stylex.props(styles.base, sizeStyles[size], variants[variant], isIconOnly && styles.iconOnly, buttonDisabled && styles.disabled, useAriaDisabled && styles.ariaDisabled, renderAsLink && styles.link, !buttonGroup && styles.pressable, buttonGroup && (buttonGroup.orientation === 'horizontal' ? groupStyles.horizontal : groupStyles.vertical), buttonGroup && (variant === 'primary' || variant === 'destructive') && (buttonGroup.orientation === 'horizontal' ? groupStyles.onSolidHorizontal : groupStyles.onSolidVertical),
  // Standalone floating buttons only — a grouped button's elevation is owned
  // by the ButtonGroup so the shared surface lifts as one unit.
  !buttonGroup && elevationStyles[elevation], width != null && dynamicStyles.width(width), xstyle);
  const sharedMergedProps = mergeProps(themeProps('button', {
    variant,
    size
  }), sharedStylexProps, className, style);
  const buttonContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [isLoadingState && /*#__PURE__*/_jsx("span", {
      ...{
        0: {
          className: "x10l6tqk x13vifvy x1o0tod xtijo5x x1ey2m1c xrvj5dj x1ku5rj1"
        },
        1: {
          className: "x10l6tqk x13vifvy x1o0tod xtijo5x x1ey2m1c xrvj5dj x1ku5rj1 xqcmdr3 xb2rp9n xskzprw x17yabm6 x14q22ui"
        }
      }[!!delaySpinner << 0],
      "aria-hidden": "true",
      children: /*#__PURE__*/_jsx(Spinner, {
        size: "sm",
        shade: "inherit"
      })
    }), /*#__PURE__*/_jsxs("span", {
      ...stylex.props(styles.contentWrapper, isLoadingState && (delaySpinner ? loadingStyles.hiddenContentDelayed : loadingStyles.hiddenContent)),
      "aria-hidden": isLoadingState || undefined,
      children: [icon && /*#__PURE__*/_jsx("span", {
        ...stylex.props(styles.iconWrapper, iconSizeStyles[size]),
        children: icon
      }), isIconOnly ? null : /*#__PURE__*/_jsx("span", {
        ...{
          className: "xb3r6kr xlyipyv xeuugli"
        },
        children: children ?? label
      }), !isIconOnly && endContent && /*#__PURE__*/_jsx("span", {
        ...{
          className: "x3nfvp2 x6s0dn4 x1heor9g"
        },
        children: endContent
      })]
    }), /*#__PURE__*/_jsx(VisuallyHidden, {
      role: "status",
      "aria-live": "polite",
      children: isLoadingState ? t('@astryx.button.loading') : ''
    })]
  });

  // aria-label is set when:
  // 1. Icon-only mode (label is the only accessible name)
  // 2. Loading state on non-icon-only (announce the button's purpose)
  // 3. Children differ from label (children are visible, label is accessible name)
  const needsAriaLabel = isIconOnly && label !== '' || isLoadingState && !isIconOnly || children != null && children !== label;
  const ariaLabelProp = needsAriaLabel ? {
    'aria-label': label
  } : null;

  // When a tooltip is attached via the hook, point aria-describedby at the
  // tooltip content (composing with any consumer-provided value).
  const describedByProp = tooltip != null ? {
    'aria-describedby': [props['aria-describedby'], tooltipHook.describedBy].filter(Boolean).join(' ') || undefined
  } : null;

  // Merge the consumer ref with the tooltip hook's trigger ref so both point at
  // the same element. mergeRefs tolerates undefined, so this is a no-op for the
  // tooltip side when no tooltip is set.
  const mergedButtonRef = mergeRefs(ref, tooltip != null ? tooltipHook.ref : undefined);
  let element;
  if (renderAsLink) {
    element = /*#__PURE__*/_jsx(LinkComponent, {
      ref: mergedButtonRef,
      href: href,
      target: target,
      rel: rel,
      ...sharedMergedProps,
      ...props,
      ...ariaLabelProp,
      ...describedByProp,
      ...edgeCompAttr,
      "aria-busy": isLoadingState || undefined,
      onClick: handleClick,
      children: buttonContent
    });
  } else {
    element = /*#__PURE__*/_jsx("button", {
      ref: mergedButtonRef,
      type: type,
      disabled: useAriaDisabled ? undefined : buttonDisabled,
      ...sharedMergedProps,
      ...props,
      ...ariaLabelProp,
      ...describedByProp,
      ...edgeCompAttr,
      "aria-busy": isLoadingState || undefined,
      "aria-disabled": useAriaDisabled || undefined,
      onClick: handleClick,
      ...(handleKeyDown ? {
        onKeyDown: handleKeyDown
      } : null),
      children: buttonContent
    });
  }
  if (tooltip) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [element, tooltipHook.renderTooltip(tooltip)]
    });
  }
  return element;
}
Button.displayName = 'Button';